import { useEffect } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
} from "@tanstack/react-router";
import { Box, CircularProgress } from "@mui/material";
import { AppShell } from "./components/AppShell";
import { ChoosePlanPage } from "./pages/ChoosePlanPage";
import { DashboardPage } from "./pages/DashboardPage";
import { EbookDetailPage } from "./pages/EbookDetailPage";
import { EbookPreviewPage } from "./pages/EbookPreviewPage";
import { EbooksPage } from "./pages/EbooksPage";
import { LoginPage } from "./pages/LoginPage";
import { PricingPage } from "./pages/PricingPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SettingsPage } from "./pages/SettingsPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminPaymentsPage } from "./pages/AdminPaymentsPage";
import { useAuthStore } from "./stores/auth-store";

function RootComponent() {
  const { loading, restore } = useAuthStore();
  useEffect(() => { void restore(); }, [restore]);
  if (loading) return <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  return <Outlet />;
}

function ProtectedShell() {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (!user.plan) return <Navigate to="/choose-plan" />;
  return <AppShell />;
}

const rootRoute = createRootRoute({ component: RootComponent });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage });
const pricingRoute = createRoute({ getParentRoute: () => rootRoute, path: "/pricing", component: PricingPage });
const choosePlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/choose-plan",
  component: function ProtectedChoosePlan() {
    const user = useAuthStore((state) => state.user);
    if (!user) return <Navigate to="/login" />;
    if (user.plan) return <Navigate to="/" />;
    return <ChoosePlanPage />;
  },
});
const previewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview",
  validateSearch: (search: Record<string, unknown>) => ({
    ebookId: typeof search.ebookId === "string" ? search.ebookId : "",
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: function ProtectedPreview() {
    const user = useAuthStore((state) => state.user);
    const { token } = previewRoute.useSearch();
    return user || token ? <EbookPreviewPage /> : <Navigate to="/login" />;
  },
});
const appRoute = createRoute({ getParentRoute: () => rootRoute, id: "_app", component: ProtectedShell });
const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: DashboardPage });
type EbooksSearch = { status?: string; label?: string };
const ebooksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ebooks",
  validateSearch: (search: Record<string, unknown>): EbooksSearch => {
    const result: EbooksSearch = {};
    if (typeof search.status === "string") result.status = search.status;
    if (typeof search.label === "string") result.label = search.label;
    return result;
  },
  component: EbooksPage,
});
const ebookTabs = [
  "general",
  "content",
  "table-of-contents",
  "toolbar",
  "publish",
] as const;
const ebookRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ebooks/$ebookId",
  validateSearch: (search: Record<string, unknown>) => ({
    tab: ebookTabs.includes(search.tab as typeof ebookTabs[number])
      ? search.tab as typeof ebookTabs[number]
      : "general" as const,
  }),
  component: EbookDetailPage,
});
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: "/settings", component: SettingsPage });
const profileRoute = createRoute({ getParentRoute: () => appRoute, path: "/profile", component: ProfilePage });
const adminUsersRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/users", component: AdminUsersPage });
const adminPaymentsRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/payments", component: AdminPaymentsPage });
const adminRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin", component: () => <Navigate to="/admin/users" /> });
const routeTree = rootRoute.addChildren([
  loginRoute,
  pricingRoute,
  choosePlanRoute,
  previewRoute,
  appRoute.addChildren([dashboardRoute, ebooksRoute, ebookRoute, settingsRoute, profileRoute, adminRoute, adminUsersRoute, adminPaymentsRoute]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
